<script setup>
import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
import {debounce} from 'lodash';
import {showErrorToast, showSuccessToast} from '@utils/Toast';
import {computed, inject, nextTick, onMounted, ref, watch} from 'vue';
import ButtonComponent from '@buttons/ButtonComponent.vue';
import {ArrowLeftIcon} from '@heroicons/vue/24/solid';
import {handleErrorResponse} from '@utils/errorHandler';

const props = defineProps({
    csrf: String,
    route: String,
    routeSuccess: String,
});

// Inject Axios
const axios = inject('axios');

const delayedRedirection = debounce(() => {
    window.location.href = props.routeSuccess;
}, 1000);


const submitForm = async () => {
    if(isFormValid.value) {
        //Prepare the data to be sent
        const formData = {
            code: code.value,
        };

        try {
            //Send a POST request to the server
            const response = await axios.post(props.route, formData, {
                headers: {
                    'X-CSRF-TOKEN': props.csrf // Include CSRF token here
                }
            });
            //Handle response here
            if(response.status === 200) {
                const data = response.data;
                if(data.success === 1) {
                    showSuccessToast(data.message);
                    //Save token to local storage
                    localStorage.setItem('auth_token', data.token);

                    code.value = emptyCode.value;
                    delayedRedirection();
                } else {
                    showErrorToast(data.message);
                }
            } else {
                if(response.message) {
                    showErrorToast(response.message);
                }
            }

        } catch (error) {
            showErrorToast(handleErrorResponse(error));
            console.error('Submission error', error);
        }
    }
};

const emptyCode = ref(Array(6).fill(''));
const code = ref(Array(6).fill(''));

const inputRefs = ref([]); // This will store references to each input field

// Handle input and jump to next field
const onInput = (index) => {
    if (code.value[index].length === 1 && index < 5) {
        inputRefs.value[index + 1]?.focus();
    } else if (code.value[index].length === 0 && index > 0) {
        inputRefs.value[index - 1]?.focus();
    }
};

const onPaste = (e) => {
    e.preventDefault(); // Prevent default paste behavior

    const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');

    // Ensure Vue detects changes using splice()
    pastedData.forEach((char, index) => {
        if (index < code.value.length) {
            code.value.splice(index, 1, char);
        }
    });

    // Focus the last filled input
    inputRefs.value[pastedData.length - 1]?.focus();
};


const isFormValid = computed(() => {
    // Regex to match alphabetic characters only
    const codeRegex = /^[A-Za-z0-9]+$/;
    // Check each value in the code array
    return code.value.every(value => value.trim() !== '' && codeRegex.test(value));
});

onMounted(async () => {
    await nextTick(); // Wait for all DOM updates to finish
});

</script>

<template>
    <div class="text-center container md:max-w-4xl">
        <div class="wrap-form">
            <form :action="route" id="frmClientLogin" method="post"
                  @submit.prevent="submitForm"
            >
                <div class="flex flex-col justify-between py-6 mb-6 md:mb-0 text-center">
                    <div class="mb-6 md:mb-10">
                        <div class="font-bold text-3xl-plus mb-6 text-center leading-tight">Activate
                            Account</div>
                        <div class="text-xl mb-6 md:mb-10 leading-tight">To unlock your
                            account please type the 6 digit access code we<br />provided to you in your activation
                            email below.</div>

                        <div class="form-row flex items-center justify-center gap-6">
                            <div
                                v-for="(digit, index) in code"
                                :key="index"
                                class="aspect-square w-16"
                            >
                                <input
                                    type="text"
                                    class="form-control h-full text-center text-3xl font-bold"
                                    v-model="code[index]"
                                    name="code[]"
                                    maxlength="1"
                                    autocomplete="off"
                                    :ref="el => inputRefs[index] = el"
                                    @input="onInput(index)"
                                    @paste="onPaste"
                                >
                            </div>
                        </div>
                    </div>
                    <div class="container max-w-100 md:w-100 text-center mx-auto">
                        <SubmitButtonLargeComponent
                            title="Activate Account"
                            btn-class="btn-green-5 w-full !py-4"
                            text-size="text-base"
                            :disabled="!isFormValid"
                        >
                        </SubmitButtonLargeComponent>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-4;
    }
    &-control {
        @apply bg-white py-3;
    }
}
</style>

